import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";

/**
 * SessionList 템플릿 Props
 */
export type SessionListProps = {
  // 상태
  sessions: any[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: any;
  snackbarOpen: boolean;
  snackbarMessage: string;

  // 핸들러
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onFilterChange: (filters: any) => void;
  onResetFilters: () => void;
  onRefresh: () => void;
  onExport: () => void;
  onViewSession: (session: any) => void;
  onSnackbarClose: () => void;
};

/**
 * 채팅 세션 리스트 템플릿
 *
 * @description 채팅 세션 목록을 표시하고 관리하는 템플릿 컴포넌트
 * @param props SessionListProps
 * @returns JSX.Element
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-20
 */
export const SessionList = (props: SessionListProps) => {
  const {
    sessions,
    loading,
    error,
    pagination,
    filters,
    snackbarOpen,
    snackbarMessage,
    onPageChange,
    onLimitChange,
    onFilterChange,
    onResetFilters,
    onRefresh,
    onExport,
    onViewSession,
    onSnackbarClose,
  } = props;

  const [localFilters, setLocalFilters] = useState({
    status: filters.status || "",
    category: filters.category || "",
    startDate: filters.startDate || "",
    endDate: filters.endDate || "",
    search: filters.search || "",
  });

  // 필터 적용
  const handleApplyFilters = () => {
    onFilterChange(localFilters);
  };

  // 필터 초기화
  const handleClearFilters = () => {
    const emptyFilters = {
      status: "",
      category: "",
      startDate: "",
      endDate: "",
      search: "",
    };
    setLocalFilters(emptyFilters);
    onResetFilters();
  };

  // 세션 상태 색상
  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "CLOSED":
        return "default";
      case "PAUSED":
        return "warning";
      default:
        return "default";
    }
  };

  // 세션 상태 텍스트
  const getStatusText = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "활성";
      case "CLOSED":
        return "종료";
      case "PAUSED":
        return "일시정지";
      default:
        return status;
    }
  };

  // 카테고리 텍스트
  const getCategoryText = (category: string) => {
    switch (category) {
      case "GENERAL":
        return "일반문의";
      case "TECHNICAL":
        return "기술지원";
      case "BILLING":
        return "요금문의";
      case "SERVICE":
        return "서비스소개";
      default:
        return category || "기타";
    }
  };

  return (
    <Box>
      {/* 필터 섹션 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          필터
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="검색"
              value={localFilters.search}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, search: e.target.value })
              }
              placeholder="세션 제목, 사용자명"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>상태</InputLabel>
              <Select
                value={localFilters.status}
                onChange={(e) =>
                  setLocalFilters({ ...localFilters, status: e.target.value })
                }
              >
                <MenuItem value="">전체</MenuItem>
                <MenuItem value="ACTIVE">활성</MenuItem>
                <MenuItem value="CLOSED">종료</MenuItem>
                <MenuItem value="PAUSED">일시정지</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>카테고리</InputLabel>
              <Select
                value={localFilters.category}
                onChange={(e) =>
                  setLocalFilters({ ...localFilters, category: e.target.value })
                }
              >
                <MenuItem value="">전체</MenuItem>
                <MenuItem value="GENERAL">일반문의</MenuItem>
                <MenuItem value="TECHNICAL">기술지원</MenuItem>
                <MenuItem value="BILLING">요금문의</MenuItem>
                <MenuItem value="SERVICE">서비스소개</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="시작일"
              value={localFilters.startDate}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, startDate: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="종료일"
              value={localFilters.endDate}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, endDate: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<FilterIcon />}
                onClick={handleApplyFilters}
                size="small"
              >
                적용
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                size="small"
              >
                초기화
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 컨트롤 헤더 */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">총 {pagination.total}개의 세션</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton onClick={onRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={onExport}
            disabled={loading}
          >
            내보내기
          </Button>
        </Box>
      </Box>

      {/* 테이블 */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>세션 ID</TableCell>
              <TableCell>제목</TableCell>
              <TableCell>사용자</TableCell>
              <TableCell>카테고리</TableCell>
              <TableCell>메시지 수</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>시작 시간</TableCell>
              <TableCell>마지막 활동</TableCell>
              <TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : sessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">
                    조회된 세션이 없습니다.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sessions.map((session) => (
                <TableRow key={session.sessionId} hover>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {session.sessionUuid?.slice(0, 8)}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {session.sessionTitle}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {session.user?.name || "게스트"}
                      </Typography>
                      {session.user?.email && (
                        <Typography variant="caption" color="textSecondary">
                          {session.user.email}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getCategoryText(session.primaryCategory)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {session.totalMessages}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(session.sessionStatus)}
                      size="small"
                      color={getStatusColor(session.sessionStatus) as any}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(session.startedAt).toLocaleString("ko-KR")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(session.lastActivityAt).toLocaleString("ko-KR")}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => onViewSession(session)}
                      title="세션 상세 보기"
                    >
                      <ViewIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* 페이지네이션 */}
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1} // MUI는 0-based, 우리는 1-based
          onPageChange={(_, newPage) => onPageChange(newPage + 1)}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={(e) => onLimitChange(parseInt(e.target.value))}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="페이지당 행 수:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} / 총 ${count}개`
          }
        />
      </TableContainer>

      {/* 스낵바 알림 */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={onSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={onSnackbarClose}
          severity={error ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

